import { useContext, useEffect, useMemo, useState } from 'react'
import {
  MRT_ShowHideColumnsButton,
  MRT_ToggleDensePaddingButton,
  MRT_ToggleFiltersButton,
  MRT_ToggleFullScreenButton,
  MRT_ToggleGlobalFilterButton
} from 'material-react-table'
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, TextField, Tooltip } from '@mui/material'
import SaveAltIcon from '@mui/icons-material/SaveAlt'
import AddBusinessOutlinedIcon from '@mui/icons-material/AddBusinessOutlined'
import { enqueueSnackbar } from 'notistack'

import { AppContext } from '/providers/app-context'
import { addChildClient } from '/providers/studio-api'
import ActionErrorDialog from '/components/action-error-dialog'
import useActionErrorDialog from '/components/use-action-error-dialog'
import StudioTable, { useStudioTable } from '../studio-table'

const emptyChildClient = {
  name: "",
  main_contact_name: "",
  main_contact_email: "",
  main_contact_phone: ""
}

const ClientAdmin = () => {
  const { apiSource, apiKey, client, refreshClient, setProcessing, selectedClientId } = useContext(AppContext)

  const [dialogOpen, setDialogOpen] = useState(false)
  const [childClient, setChildClient] = useState(emptyChildClient)
  const { actionError, errorDialogOpen, setErrorDialogOpen, showActionError } = useActionErrorDialog()

  useEffect(() => {
    refreshClient()
  }, [selectedClientId])

  const columns = useMemo(() => [
    { accessorKey: 'client_id', header: 'Client ID', size: 90 },
    { accessorKey: 'name', header: 'Client name', size: 180 },
    { accessorKey: 'main_contact_name', header: 'Main contact', size: 180 },
    { accessorKey: 'main_contact_email', header: 'Main contact email', size: 220 },
    { accessorKey: 'main_contact_phone', header: 'Main contact phone', size: 160 },
    { accessorKey: 'creation_date', header: 'Creation date', size: 120 },
  ], [])

  const table = useStudioTable({
    tableKey: 'clients',
    columns,
    data: client?.child_clients ?? [],
    enableRowActions: false,
    enablePagination: false,
    enableBottomToolbar: false,
    enableStickyHeader: true,
    enableStickyFooter: true,
    initialState: { showColumnFilters: false },
    positionToolbarAlertBanner: "none",
    renderToolbarInternalActions: ({ table }) => (
      <Box>
        <MRT_ToggleGlobalFilterButton table={table} />
        <MRT_ToggleFiltersButton table={table} />
        <MRT_ShowHideColumnsButton table={table} />
        <MRT_ToggleDensePaddingButton table={table} />
        <MRT_ToggleFullScreenButton table={table} />
        <Tooltip title="Export"><IconButton><SaveAltIcon /></IconButton></Tooltip>
      </Box>
    ),
    renderTopToolbarCustomActions: () => (
      <div className='flex flex-row items-start gap-5'>
        <Button startIcon={<AddBusinessOutlinedIcon className='mr-2' />} onClick={() => setDialogOpen(true)}>Add Client</Button>
      </div>
    )
  })

  const updateField = field => event => {
    setChildClient({
      ...childClient,
      [field]: event.target.value
    })
  }

  const saveChildClient = async () => {
    setProcessing(true)
    enqueueSnackbar({ message: 'Adding child client', variant: 'light' })
    const result = await addChildClient({ apiSource, apiKey, childClient })

    if (result.ok) {
      enqueueSnackbar({ message: 'Child client added', variant: 'success' })
      setDialogOpen(false)
      setChildClient(emptyChildClient)
      await refreshClient()
    } else {
      showActionError(result.error, 'Error adding child client')
    }

    setProcessing(false)
  }

  const valid = childClient.name && childClient.main_contact_name && childClient.main_contact_email && childClient.main_contact_phone

  return (
    <>
      <Box sx={{ height: '100%', width: '100%', minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <StudioTable table={table} />
      </Box>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth='sm'>
        <DialogTitle>Add child client</DialogTitle>
        <DialogContent>
          <div className='flex flex-col gap-4 pt-2'>
            <TextField label='Client name' value={childClient.name} onChange={updateField("name")} size='small' required />
            <TextField label='Main contact name' value={childClient.main_contact_name} onChange={updateField("main_contact_name")} size='small' required />
            <TextField label='Main contact email' value={childClient.main_contact_email} onChange={updateField("main_contact_email")} size='small' required />
            <TextField label='Main contact phone' value={childClient.main_contact_phone} onChange={updateField("main_contact_phone")} size='small' required />
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant='contained' onClick={saveChildClient} disabled={!valid}>Create client</Button>
        </DialogActions>
      </Dialog>
      <ActionErrorDialog title="Child client creation failed" open={errorDialogOpen} setOpen={setErrorDialogOpen} error={actionError} />
    </>
  )
}

export default ClientAdmin
